import { baseApi } from './baseApi'
import type { SaveChargeConfigRequest } from 'components/lab/payer/chargeRules/chargeRule'
import type {
  PayerChargeRulesList,
  PayerChargeRulesListQuery,
} from 'ts-back-end/src/business/payer/dto/payer-charge-rule.dto'

/** One configuration of one payer, by the id the listing and the payer carry. */
export type DeleteChargeConfigRequest = { payerId: number; id: string }

export type ChargeRuleQuery = Partial<Omit<PayerChargeRulesListQuery, 'testType'>> & {
  testType?: string
}

/**
 * The dedicated charge-rule endpoints. Writes go here rather than through the
 * payer update endpoint so a screen that edits billing rules cannot send any
 * other payer field; the server checks the one configuration against the
 * payer's whole set before it lands.
 */
export const payerChargeRulesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPayerChargeRules: builder.query<PayerChargeRulesList, ChargeRuleQuery>({
      query: (params) => ({ url: `/lab/payer-charge-rules`, params }),
      providesTags: ['PayerChargeRules'],
    }),
    // With an `id` the body edits that configuration; without one it adds a row.
    savePayerChargeRule: builder.mutation<void, SaveChargeConfigRequest>({
      query: (body) => ({ method: `POST`, url: `/lab/payer-charge-rules`, body }),
      // The payer screen reads the same rules off the payer aggregate, so both
      // views have to be refreshed by either write path.
      invalidatesTags: (result, error, { payerId }) => [
        'PayerChargeRules',
        { type: 'LabPayer', id: String(payerId) },
        { type: 'PayerHistory', id: String(payerId) },
      ],
    }),
    deletePayerChargeRule: builder.mutation<void, DeleteChargeConfigRequest>({
      query: ({ payerId, id }) => ({
        method: `DELETE`,
        url: `/lab/payer-charge-rules/${payerId}/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (result, error, { payerId }) => [
        'PayerChargeRules',
        { type: 'LabPayer', id: String(payerId) },
        { type: 'PayerHistory', id: String(payerId) },
      ],
    }),
  }),
})

export const {
  useGetPayerChargeRulesQuery,
  useLazyGetPayerChargeRulesQuery,
  useSavePayerChargeRuleMutation,
  useDeletePayerChargeRuleMutation,
} = payerChargeRulesApi
